import React from 'react';
import { KPICard, DataTable, SectionHeader, StatusPill } from '../components';

export function OverviewPage({ rows, dashboard }) {
  const a = rows.analytics || {};
  const recentOrders = (rows.orders || []).slice(0, 6);

  return (
    <div>
      <SectionHeader
        title="Overview"
        subtitle={`Last 30 days · ${dashboard?.dna?.vertical || 'generic'} cockpit`}
      />

      {/* KPI row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 14, marginBottom: 28,
      }}>
        <KPICard label="Revenue · 30d"   value={a.revenue_30d}   prev={a.revenue_prev}   format="currency" testid="kpi-revenue" />
        <KPICard label="New customers"   value={a.customers_30d} prev={a.customers_prev}                  testid="kpi-customers" />
        <KPICard label="Orders"          value={a.orders_30d}    prev={a.orders_prev}                     testid="kpi-orders" />
        <KPICard label="Conversion rate" value={a.conversion}    prev={a.conversion_prev} format="percent" testid="kpi-conversion" />
      </div>

      {/* Sparkline + sources side by side */}
      <div style={{
        display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 16,
        marginBottom: 28,
      }}>
        <RevenueSparkline series={a.revenue_series || []} />
        <TopSources sources={a.top_sources || []} />
      </div>

      {/* Recent activity */}
      <SectionHeader title="Recent activity" />
      <DataTable
        testid="dash-recent-orders"
        columns={[
          { key: 'ref',           label: 'Ref',      nowrap: true },
          { key: 'customer_name', label: 'Customer' },
          { key: 'product_name',  label: 'Item' },
          { key: 'amount',        label: 'Amount', nowrap: true,
            render: r => `${r.currency || '€'}${(r.amount || 0).toFixed(2)}` },
          { key: 'status',        label: 'Status',
            render: r => <StatusPill value={r.status} /> },
          { key: 'placed_at',     label: 'When', nowrap: true,
            render: r => (r.placed_at || '').slice(0, 10) },
        ]}
        rows={recentOrders}
        emptyText="No orders yet — once your build goes live, recent transactions will show here."
      />
    </div>
  );
}

/* ============================================================================ */
/* Revenue sparkline — pure SVG, no deps                                       */
/* ============================================================================ */

function RevenueSparkline({ series }) {
  const data = (series && series.length > 0) ? series : Array(30).fill(0);
  const W = 600, H = 160, PAD = 16;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const step = (W - PAD * 2) / Math.max(1, data.length - 1);
  const points = data.map((v, i) => [
    PAD + i * step,
    H - PAD - ((v - min) / range) * (H - PAD * 2),
  ]);
  const path = points.reduce((acc, [x, y], i) => acc + (i === 0 ? `M${x},${y}` : ` L${x},${y}`), '');
  const area = `${path} L${points[points.length-1][0]},${H-PAD} L${points[0][0]},${H-PAD} Z`;
  return (
    <div style={{
      padding: 18, background: 'var(--dash-bg-card)',
      border: '1px solid var(--dash-border)', borderRadius: 'var(--dash-radius)',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
        textTransform: 'uppercase', color: 'var(--dash-text-muted)',
        marginBottom: 12,
      }}>
        Revenue · last 30 days
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: 160, display: 'block' }}>
        <defs>
          <linearGradient id="rev-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%"  stopColor="var(--dash-primary)" stopOpacity="0.35" />
            <stop offset="100%" stopColor="var(--dash-primary)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#rev-fill)" />
        <path d={path} fill="none" stroke="var(--dash-primary)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function TopSources({ sources }) {
  return (
    <div style={{
      padding: 18, background: 'var(--dash-bg-card)',
      border: '1px solid var(--dash-border)', borderRadius: 'var(--dash-radius)',
    }}>
      <div style={{
        fontSize: 11, fontWeight: 700, letterSpacing: '0.10em',
        textTransform: 'uppercase', color: 'var(--dash-text-muted)',
        marginBottom: 12,
      }}>
        Top sources
      </div>
      {(!sources || sources.length === 0) ? (
        <div style={{
          padding: '24px 0', textAlign: 'center',
          color: 'var(--dash-text-muted)', fontSize: 12,
        }}>
          Waiting for first traffic.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sources.slice(0, 5).map(s => (
            <div key={s.name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: 'var(--dash-text)' }}>{s.name}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--dash-text-muted)' }}>
                  {Math.round((s.share || 0) * 100)}%
                </span>
              </div>
              <div style={{ height: 4, background: 'rgba(15,15,30,0.05)', borderRadius: 999 }}>
                <div style={{
                  height: '100%',
                  width: `${(s.share || 0) * 100}%`,
                  background: 'var(--dash-primary)',
                  borderRadius: 999,
                }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
